import React from 'react';
import { enqueueSnackbar } from 'notistack';

import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import WifiIcon from '@mui/icons-material/Wifi';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router';
import { sensorTypeDetails } from '../../utils/sensorProperties';
import navigationStyles from '../../styles/navigationDrawer';
import useStyles from '../../styles/location';
import { GatewaySensorType, SensorAlertsPartial, SensorLatest, VarName } from '../../services/api';
import { themeProps } from '../../styles/theme';
import { getDataBandParams } from '../../utils/dataBandParams';

interface ConfiguredSensorsListProps {
  allSensors: SensorLatest[];
  sensorAlertsPartial: SensorAlertsPartial[];
}

function ConfiguredSensorsList({
  allSensors,
  sensorAlertsPartial,
}: ConfiguredSensorsListProps): JSX.Element {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { classes: navigationClasses } = navigationStyles();

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      enqueueSnackbar('Sensor ID copied to clipboard', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to copy', { variant: 'error', persist: true });
    }
  };

  return (
    <Box>
      {allSensors?.map((sensor) => {
        if (!sensor?.id) return null;
        const sensorId = sensor.id;

        const isGateway = Object.values(GatewaySensorType).includes(
          sensor?.type as GatewaySensorType
        );
        const isEnabled = isGateway || !!sensor?.gateway;
        const typeDetails = sensor?.type ? sensorTypeDetails[sensor.type] : null;

        const Icon: typeof SvgIcon = typeDetails?.icon ?? WifiIcon;
        const tooltipText = typeDetails?.label ?? 'Wifi';

        const onlineStatusValue = sensor?.data?.find(
          (d) => d.varName === VarName.OnlineStatus
        )?.value;

        const sensorStatusBandParams =
          isEnabled && onlineStatusValue !== undefined
            ? getDataBandParams(VarName.OnlineStatus, onlineStatusValue)
            : null;

        const sensorStatusColor = sensorStatusBandParams?.color ?? themeProps.colors.grey;
        const sensorStatusDescription =
          sensorStatusBandParams?.label ?? (isEnabled ? 'Unknown' : 'Disabled');

        const handleClick = () => {
          navigate(`/sensors/${sensorId}`);
        };

        const sensorAlerts = sensorAlertsPartial.find((alertItem) => alertItem.id === sensorId);
        const hasActiveAlert =
          sensorAlerts &&
          sensorAlerts.alerts.find((alertParam) => alertParam.alertStatus?.isActive === true);
        const AlertIcon = hasActiveAlert ? NotificationsActiveIcon : NotificationsIcon;

        return (
          <Grid container className={classes.sensorList} key={sensorId} wrap="nowrap">
            <Grid size={{ xs: 1, sm: 1, md: 1 }} sx={{ padding: '0' }}>
              <Icon className={classes.icon} />
            </Grid>
            <Grid size={{ xs: 3, sm: 3, md: 4 }}>
              <Typography variant="body1">{sensor?.name}</Typography>
            </Grid>
            <Grid
              size={{ xs: 8, sm: 8, md: 7 }}
              sx={{ display: 'flex', alignItems: 'center', background: 'none' }}
              className={navigationClasses.navLink}
            >
              <Grid container spacing={2} wrap="nowrap">
                <Grid
                  size={{ md: 2, sm: 2, xs: 2 }}
                  className={classes.sensorGridItem}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}
                >
                  {sensorAlerts ? (
                    <AlertIcon fontSize="small" color={hasActiveAlert ? 'error' : 'success'} />
                  ) : (
                    ''
                  )}
                </Grid>
                <Grid size={{ md: 8, sm: 8, xs: 8 }} sx={{ display: 'flex' }} onClick={handleClick}>
                  <Tooltip title={sensorStatusDescription}>
                    <FiberManualRecordIcon
                      style={{
                        marginRight: '5px',
                        color: sensorStatusColor,
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={tooltipText}>
                    <Typography variant="body1">{sensorId}</Typography>
                  </Tooltip>
                </Grid>
                <Grid size={{ md: 2, sm: 2, xs: 2 }} sx={{ padding: '0' }}>
                  <Tooltip title="Copy sensor id">
                    <IconButton
                      size="small"
                      aria-label="Copy sensor id"
                      onClick={() => handleCopy(sensorId)}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon color="secondary" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
      })}
    </Box>
  );
}

export default ConfiguredSensorsList;
